import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CryptoJS from 'crypto-js';
import { setAdminData, addUser, updateUser, removeUser } from '../store/adminSlice';
import { PW_Users_API, PW_User_Save_API, PW_User_Delete_API } from '../Server/ApiV2';
import Spinner from './Spinner';

const ENC_KEY = 'PW-S3CR3T-K3Y-2026-X9Z';
const decrypt = (cipher) => {
  try { return CryptoJS.AES.decrypt(cipher || '', ENC_KEY).toString(CryptoJS.enc.Utf8) || ''; }
  catch { return ''; }
};

export default function Users() {
  const dispatch = useDispatch();
  const { users, userTypes } = useSelector(s => s.admin);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await PW_Users_API();
      if (cancelled) return;
      if (res?.data?.success) {
        const u = res.data.data;
        dispatch(setAdminData({ users: u.users, userStoreRights: u.userStoreRights, userTypes: u.userTypes }));
      }
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', username: '', password: '', userTypeId: 'OPERATOR', email: '', phone: '', status: 'Active' });

  const resetForm = () => setForm({ name: '', username: '', password: '', userTypeId: 'OPERATOR', email: '', phone: '', status: 'Active' });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.username.trim()) e.username = 'Username is required';
    if (!form.password.trim()) e.password = 'Password is required';
    if (!form.userTypeId) e.userTypeId = 'User Type is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const refreshUsers = async () => {
    const res = await PW_Users_API();
    if (res?.data?.success) {
      const u = res.data.data;
      dispatch(setAdminData({ users: u.users, userStoreRights: u.userStoreRights, userTypes: u.userTypes }));
    }
  };

  const handleAdd = async () => {
    if (!validate()) return;
    const id = `USR-${Date.now()}`;
    const userData = { ...form, id, createdAt: new Date().toISOString().split('T')[0] };
    try {
      await PW_User_Save_API(userData);
      await refreshUsers();
    } catch (err) { console.error('[Users] Save failed:', err); }
    resetForm(); setErrors({});
    setShowAdd(false);
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setErrors({});
    setForm({ name: user.name, username: user.username, password: decrypt(user.password), userTypeId: user.userTypeId, email: user.email || '', phone: user.phone || '', status: user.status });
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    const updates = { id: editingId, name: form.name, username: form.username, password: form.password, userTypeId: form.userTypeId, email: form.email, phone: form.phone, status: form.status };
    try {
      await PW_User_Save_API(updates);
      await refreshUsers();
    } catch (err) { console.error('[Users] Update failed:', err); }
    setEditingId(null);
    resetForm(); setErrors({});
  };

  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
    setDeleting(user.id);
    try {
      await PW_User_Delete_API(user.id);
      dispatch(removeUser(user.id));
      await refreshUsers();
    } catch (err) { console.error('[Users] Delete failed:', err); }
    setDeleting(null);
  };

  const getUserTypeName = (id) => userTypes.find(t => t.id === id)?.name || id;

  const [filter, setFilter] = useState('');
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    let list = users;
    if (q) {
      list = users.filter(u =>
        u.id?.toLowerCase().includes(q) ||
        u.name?.toLowerCase().includes(q) ||
        u.username?.toLowerCase().includes(q) ||
        decrypt(u.password)?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q) ||
        u.status?.toLowerCase().includes(q) ||
        getUserTypeName(u.userTypeId)?.toLowerCase().includes(q) ||
        u.createdAt?.toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let va = '', vb = '';
      if (sortCol === 'typeName') {
        va = getUserTypeName(a.userTypeId); vb = getUserTypeName(b.userTypeId);
      } else {
        va = a[sortCol] || ''; vb = b[sortCol] || '';
      }
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, filter, sortCol, sortDir, userTypes]);

  const sortIcon = (col) => {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Users</h2>
          <div className="page-header-sub">Manage system users and their roles</div>
        </div>
        <button className="btn btn-gold" onClick={() => { resetForm(); setShowAdd(true); setEditingId(null); }}>+ Add User</button>
      </div>

      {(showAdd || editingId) && (
        <div className="card mb-24">
          <div className="card-title gold">{editingId ? 'Edit User' : 'New User'}</div>
          <div className="card-body">
            <div className="form-grid mb-16">
              <div className="form-group">
                <label className="form-label">Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" style={errors.name ? { borderColor: '#ef4444' } : {}} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                {errors.name && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.name}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Username <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" style={errors.username ? { borderColor: '#ef4444' } : {}} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
                {errors.username && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.username}</span>}
              </div>
            </div>
            <div className="form-grid mb-16">
              <div className="form-group">
                <label className="form-label">Password <span style={{ color: '#ef4444' }}>*</span></label>
                <input className="form-input" style={errors.password ? { borderColor: '#ef4444' } : {}} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                {errors.password && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.password}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">User Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select className="form-select" style={errors.userTypeId ? { borderColor: '#ef4444' } : {}} value={form.userTypeId} onChange={e => setForm({ ...form, userTypeId: e.target.value })}>
                  {userTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {errors.userTypeId && <span style={{ color: '#ef4444', fontSize: 12 }}>{errors.userTypeId}</span>}
              </div>
            </div>
            <div className="form-grid mb-16">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            {editingId && (
              <div className="form-group mb-16">
                <label className="form-label">Status</label>
                <select className="form-select" style={{ width: 200 }} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success" onClick={editingId ? handleUpdate : handleAdd}>
                {editingId ? 'Update' : 'Create'} User
              </button>
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setEditingId(null); resetForm(); setErrors({}); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">
          Users
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b6b80' }}>{filtered.length} of {users.length} users</span>
        </div>
        <div style={{ padding: '8px 16px' }}>
          <input
            className="form-input"
            placeholder="Search users..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ maxWidth: 300 }}
          />
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>ID{sortIcon('id')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{sortIcon('name')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>Username{sortIcon('username')}</th>
                  <th>Password</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('typeName')}>Type{sortIcon('typeName')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{sortIcon('email')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>Phone{sortIcon('phone')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIcon('status')}</th>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>Created{sortIcon('createdAt')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id}>
                    <td data-label="ID" className="text-sm text-muted">{u.id}</td>
                    <td data-label="Name" className="fw-600">{u.name}</td>
                    <td data-label="Username">{u.username}</td>
                    <td data-label="Password" className="text-sm">{decrypt(u.password)}</td>
                    <td data-label="Type"><span className="chip chip-purple">{getUserTypeName(u.userTypeId)}</span></td>
                    <td data-label="Email" className="text-sm">{u.email}</td>
                    <td data-label="Phone" className="text-sm">{u.phone}</td>
                    <td data-label="Status"><span className={u.status === 'Active' ? 'status-active' : 'status-inactive'}>{u.status}</span></td>
                    <td data-label="Created" className="text-sm text-muted">{u.createdAt?.split('T')[0]}</td>
                    <td data-label="Actions" style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => handleEdit(u)}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{ color: '#ef4444', borderColor: '#ef4444' }}
                        disabled={deleting === u.id}
                        onClick={() => handleDelete(u)}>
                        {deleting === u.id ? '...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 24, color: '#6b6b80' }}>No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
